import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Quote } from "lucide-react";
import StoreBadges from "@/components/StoreBadges";
import FreeQuoteCta from "@/components/FreeQuoteCta";
import { useSEO } from "@/lib/useSEO";
import { langAlternates, langCanonical } from "@/lib/langSeo";
import { faqPage, webPage, breadcrumb } from "@/lib/jsonLd";
import { FAQ_BY_PAGE, getFaq } from "@/data/faq";
import { getSubPage } from "@/data/subPages";
import { useT } from "@/i18n/LanguageContext";
import { useSiteMedia } from "@/lib/useSiteMedia";

/**
 * Templated SEO landing page used by all six sub-pages. Content is pulled
 * from /data/subPages.js based on the current language; only the slug is
 * passed in by each consumer page.
 *
 * Philosopher portraits (Phase 3b-3): sourced via the Site Media Registry
 * so the Content Vault is the single source of truth for these images
 * across the site. The static fallback in `sage.image` keeps SSR-time
 * JSON-LD stable and prevents an empty <img> on first paint before the
 * registry answers.
 */

// Sub-page slug → Site Media slot id. Reuses the existing philosopher
// portrait slots (also driving the homepage guide-cards) so the founder
// only manages one asset per sage.
const SAGE_SLOT_BY_SLUG = {
  "marcus-aurelius-ai": "home.guide.marcus",
  "seneca-ai":          "home.guide.seneca",
  "epictetus-ai":       "home.guide.epictetus",
};

export default function SubLanding({ slug }) {
  const { t, lang } = useT();
  const content = getSubPage(slug, lang);
  const faqs = getFaq(FAQ_BY_PAGE[slug] || [], lang);

  const canonical = `https://stoicos.ai/${slug}`;
  const eyebrow      = content?.eyebrow || "";
  const title        = content?.title || "";
  const description  = content?.description || "";
  const h1           = content?.h1 || { lead: "", accent: "" };
  const lede         = content?.lede || "";
  const sections     = content?.sections || [];
  const sage         = content?.sage;
  const personJsonLd = content?.personJsonLd;

  // Resolve the sage portrait through the Site Media Registry (falls
  // back to the static URL baked into `sage.image` when the registry
  // has no vault asset assigned yet). Hook runs unconditionally to
  // respect the rules-of-hooks — safe even when there's no sage on
  // the current sub-page because the slot lookup just returns fallback.
  const sageSlot = SAGE_SLOT_BY_SLUG[slug];
  const sageMedia = useSiteMedia(sageSlot || "", {
    fallbackUrl: sage?.image || "",
    fallbackAlt: sage ? `${sage.name}, Stoic philosopher` : "",
  });
  const sageImageUrl = (sage && sageMedia.url) || sage?.image || "";

  // Update JSON-LD to point at the same URL the visible <img> uses, so
  // Google's crawler sees the vault-served image instead of the legacy
  // customer-assets host. Shallow-clone to avoid mutating imports.
  const patchedPersonJsonLd = personJsonLd
    ? { ...personJsonLd, image: sageImageUrl || personJsonLd.image }
    : null;

  const breadcrumbItems = [
    { name: "Home", url: "/" },
    { name: eyebrow, url: `/${slug}` },
  ];

  const jsonLd = [
    content ? webPage({ name: title, description, url: `/${slug}`, breadcrumbItems }) : null,
    content ? breadcrumb(breadcrumbItems) : null,
    faqs.length ? faqPage(faqs) : null,
    patchedPersonJsonLd || null,
  ].filter(Boolean);

  useSEO({
    title,
    description,
    canonical: langCanonical(`/${slug}`, lang),
    alternates: langAlternates(`/${slug}`),
    og: { title, description },
    jsonLd,
  });

  if (!content) return null;

  return (
    <main data-testid={`sublanding-${slug}`} className="relative pt-32 md:pt-40 pb-32">
      {/* HERO */}
      <section className="max-w-4xl mx-auto px-6 md:px-10">
        <Link
          to="/"
          aria-label={t("sub.backAria")}
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-fog/70 hover:text-gold transition-colors mb-10"
          data-testid={`sublanding-${slug}-back`}
        >
          <ArrowLeft size={12} strokeWidth={1.5} /> StoicOs · Virtue Guide
        </Link>
        <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">{eyebrow}</p>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-bone tracking-tight leading-[1.0]">
          {h1.lead} <br/><span className="italic text-gold">{h1.accent}</span>
        </h1>
        <p className="mt-10 text-bone/85 font-light text-lg md:text-xl leading-relaxed max-w-2xl">{lede}</p>

        <div className="mt-12">
          <StoreBadges />
        </div>
        <div className="mt-5">
          <FreeQuoteCta />
        </div>
      </section>

      <div className="gold-rule my-20 max-w-4xl mx-auto" />

      {/* SAGE PROFILE (only for sage pages) */}
      {sage && (
        <section className="max-w-4xl mx-auto px-6 md:px-10 mb-20" data-testid={`sublanding-${slug}-sage`}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-tr from-gold/0 via-gold/15 to-gold/0 blur-2xl opacity-70 pointer-events-none" aria-hidden />
                <div className="relative p-[1px] bg-gradient-to-br from-gold/60 via-gold/10 to-gold/0">
                  <div className="bg-[#050505] overflow-hidden">
                    <img
                      src={sageImageUrl}
                      alt={sageMedia.alt || `${sage.name}, Stoic philosopher`}
                      loading="lazy"
                      data-testid={`sublanding-${slug}-sage-image`}
                      data-media-source={sageMedia.source || "legacy"}
                      className="block w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="md:col-span-7">
              <p className="text-[10px] tracking-[0.28em] uppercase text-gold">{sage.role}</p>
              <p className="font-serif text-3xl md:text-4xl text-bone tracking-tight mt-2">{sage.name}</p>
              <p className="mt-1 text-[11px] tracking-[0.18em] uppercase text-fog/70">{sage.dates}</p>
              <div className="mt-6 space-y-5 text-bone/80 font-light leading-[1.85]">
                {sage.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* BODY SECTIONS */}
      <div className="max-w-3xl mx-auto px-6 md:px-10 space-y-20">
        {sections.map((s, i) => (
          <section key={i} data-testid={`sublanding-${slug}-section-${i}`}>
            <h2 className="font-serif text-3xl md:text-4xl text-bone tracking-tight leading-tight">{s.heading}</h2>
            <div className="mt-6 text-bone/85 font-light text-lg leading-[1.85] space-y-5">
              {s.body.split("\n\n").map((para, j) => (
                <p key={j}>{para}</p>
              ))}
            </div>
            {s.italicQuote && (
              <figure className="mt-8 pl-6 border-l border-gold/30">
                <Quote size={18} color="#C5A059" strokeWidth={1.25} className="mb-3" />
                <blockquote className="font-serif italic text-xl md:text-2xl text-bone leading-snug">
                  {s.italicQuote}
                </blockquote>
                {s.attrib && (
                  <figcaption className="mt-3 text-[11px] tracking-[0.22em] uppercase text-gold">
                    — {s.attrib}
                  </figcaption>
                )}
              </figure>
            )}
          </section>
        ))}
      </div>

      {/* FAQ for this page */}
      {faqs.length > 0 && (
        <section className="max-w-3xl mx-auto px-6 md:px-10 mt-24" data-testid={`sublanding-${slug}-faq`}>
          <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-5">{t("sub.commonQ")}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-bone tracking-tight leading-tight">{t("sub.peopleAsk")}</h2>
          <ul className="mt-10 space-y-8 border-t border-white/10 pt-10">
            {faqs.map((f) => (
              <li key={f.id} data-testid={`sublanding-${slug}-faq-${f.id}`}>
                <h3 className="font-serif text-xl md:text-2xl text-bone leading-snug">{f.q}</h3>
                <p className="mt-3 text-fog font-light leading-[1.85]">{f.a}</p>
              </li>
            ))}
          </ul>
          <div className="mt-10">
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-gold hover:text-gold-hover transition-colors"
              data-testid={`sublanding-${slug}-all-faq`}
            >
              {t("sub.seeAllFaq")} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 mt-24">
        <div className="p-10 border border-gold/20 bg-ink-elevated/40 text-center">
          <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-4">{t("sub.cta.eyebrow")}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-bone tracking-tight leading-tight">
            {t("sub.cta.h2a")}<span className="italic text-gold">{t("sub.cta.h2b")}</span>
          </h2>
          <p className="mt-5 text-fog font-light max-w-md mx-auto leading-relaxed">{t("sub.cta.body")}</p>
          <div className="mt-8 flex justify-center">
            <StoreBadges align="center" />
          </div>
        </div>
      </section>
    </main>
  );
}
